import { addChannelData, addTaxonomyData, isChannelDataLoaded, shapeDataForApp } from '@/utils/data';
import { sortCaseIgnore } from '@/utils/array';
import { forEach } from '@/utils/iterable/forEach';
import { getSize } from '@/utils/iterable/getSize';
import { api } from '@/utils/api';

import { EBAY, ETSY, FAIRE, SHOPIFY } from '@/constants/channels';
import { MESSAGE, NOTIFICATION } from '@/constants/notifications';
import { CHANNEL_DATA_KEYS } from '@/constants/data';
import { PROFILE_TYPE } from '@/constants/profiles';
import { ENDPOINT } from '@/constants/api';
import { FEATURE } from '@/constants/features';
import { AMOUNT } from '@/constants/product';
import { EMPTY } from '@/constants';
import { DATA } from '@/data';

import actions from '@/actions';

function* addProductType(reduction, { shopId, productType }) {
  let productTypes = reduction.getIn(['data', 'shopsData', shopId, 'productTypes'], EMPTY.LIST);

  if (productType && !productTypes.includes(productType)) {
    productTypes = productTypes.push(productType).sort(sortCaseIgnore);
  }

  return reduction.setIn(['data', 'shopsData', shopId, 'productTypes'], productTypes);
}

function* addSection(reduction, { shopId, section }) {
  let sections = reduction.getIn(['data', 'shopsData', shopId, 'sections'], EMPTY.MAP);

  function updateById(byId = EMPTY.MAP) {
    return byId.set(section, section);
  }

  function updateOptions(options = EMPTY.LIST) {
    return options.push(section);
  }

  function sortSections(sectionsById) {
    return function sort(x, y) {
      return sortCaseIgnore(sectionsById.get(x), sectionsById.get(y));
    };
  }

  if (!sections.hasIn(['byId', section])) {
    const byId = sections.get('byId').update(updateById);
    const options = sections.get('options').update(updateOptions).sort(sortSections(byId));
    sections = sections.set('byId', byId).set('options', options);
  }

  return reduction.setIn(['data', 'shopsData', shopId, 'sections'], sections);
}

function* addVariations(reduction, { option, shopId, variant }) {
  let state = reduction;

  if (shopId) {
    const channel = state.getIn(['shops', 'byId', shopId, 'channel']);
    const db = state.getIn(['shops', 'byId', shopId, 'db']);

    if (variant) {
      state = state.setIn(['data', 'loading', 'variations', shopId], true);

      yield function effect(dispatch) {
        if (option) {
          api.variations
            .addOption({ channel, db, option, shopId, variant })
            .then(function onSuccess(data) {
              dispatch(actions.data.getVariations.fulfilled({ channel, data, shopId }));
            })
            .catch(function onFail(error) {
              console.error(error);

              dispatch(
                actions.notifications.add({
                  type: NOTIFICATION.ERROR,
                  message: MESSAGE.FAIL.SAVE_OPTION,
                })
              );

              dispatch(actions.data.addVariations.rejected({ shopId }));
            });
        } else {
          api.variations
            .addVariant({ channel, db, shopId, variant })
            .then(function onSuccess(data) {
              dispatch(actions.data.getVariations.fulfilled({ channel, data, shopId }));
            })
            .catch(function onFail(error) {
              console.error(error);

              dispatch(
                actions.notifications.add({
                  type: NOTIFICATION.ERROR,
                  message: MESSAGE.FAIL.SAVE_VARIANT,
                })
              );

              dispatch(actions.data.addVariations.rejected({ shopId }));
            });
        }
      };
    }
  }

  return state;
}

function* addVariationsRejected(reduction, { shopId }) {
  return reduction.deleteIn(['data', 'loading', 'variations', shopId], true);
}

function* addVendor(reduction, { shopId, vendor }) {
  let vendors = reduction.getIn(['data', 'shopsData', shopId, 'vendors'], EMPTY.LIST);

  if (vendor && !vendors.includes(vendor)) {
    vendors = vendors.push(vendor).sort(sortCaseIgnore);
  }

  return reduction.setIn(['data', 'shopsData', shopId, 'vendors'], vendors);
}

function* deleteVariations(reduction, { option: optionId, shopId, variant: variantId }) {
  let state = reduction;
  const channel = state.getIn(['shops', 'byId', shopId, 'channel']);
  const db = state.getIn(['shops', 'byId', shopId, 'db']);

  if (variantId) {
    const variantIndex = state
      .getIn(['data', 'shopsData', shopId, 'variations', 'variants'], EMPTY.LIST)
      .findIndex(function findVariant(item) {
        return item.get('id') === variantId;
      });

    if (variantIndex === -1) return state;

    if (optionId) {
      const variantName = state.getIn(['data', 'shopsData', shopId, 'variations', 'variants', variantIndex, 'value']);
      const optionIndex = state
        .getIn(['data', 'shopsData', shopId, 'variations', 'options', variantName], EMPTY.LIST)
        .findIndex(function findOption(item) {
          return item.get('id') === optionId;
        });

      if (optionIndex === -1) return state;

      state = state
        .setIn(['data', 'loading', 'variations', shopId], true)
        .setIn(['data', 'shopsData', shopId, 'variations', 'options', variantName, optionIndex, 'disabled'], true);

      yield function effect(dispatch) {
        api.variations
          .deleteOption({ channel, db, option: optionId, shopId, variant: variantId })
          .then(function onSuccess(data) {
            dispatch(actions.data.getVariations.fulfilled({ channel, data, shopId }));
          })
          .catch(function onFail(error) {
            console.error(error);

            dispatch(
              actions.notifications.add({
                type: NOTIFICATION.ERROR,
                message: MESSAGE.FAIL.DELETE_OPTION,
              })
            );

            dispatch(
              actions.data.deleteVariations.rejected({
                optionId,
                optionIndex,
                shopId,
                variantId,
                variantIndex,
                variantName,
              })
            );
          });
      };
    } else {
      state = state
        .setIn(['data', 'loading', 'variations', shopId], true)
        .setIn(['data', 'shopsData', shopId, 'variations', 'variants', variantIndex, 'disabled'], true);

      yield function effect(dispatch) {
        api.variations
          .deleteVariant({ channel, db, shopId, variant: variantId })
          .then(function onSuccess(data) {
            dispatch(actions.data.getVariations.fulfilled({ channel, data, shopId }));
          })
          .catch(function onFail(error) {
            console.error(error);

            dispatch(
              actions.notifications.add({
                type: NOTIFICATION.ERROR,
                message: MESSAGE.FAIL.DELETE_VARIANT,
              })
            );

            dispatch(actions.data.deleteVariations.rejected({ shopId, variantId, variantIndex }));
          });
      };
    }
  }

  return state;
}

function* deleteVariationsRejected(reduction, payload) {
  const { optionId, shopId, variantId, variantName } = payload;
  let { optionIndex, variantIndex } = payload;
  let state = reduction;

  if (variantId) {
    if (state.getIn(['data', 'shopsData', shopId, 'variations', 'variants', variantIndex, 'id']) !== variantId) {
      variantIndex = state
        .getIn(['data', 'shopsData', shopId, 'variations', 'variants'], EMPTY.LIST)
        .findIndex(function findVariant(item) {
          return item.get('id') === variantId;
        });

      if (variantIndex === -1) return state;
    }

    if (optionId) {
      if (
        state.getIn(['data', 'shopsData', shopId, 'variations', 'options', variantName, optionIndex, 'id']) !== optionId
      ) {
        optionIndex = state
          .getIn(['data', 'shopsData', shopId, 'variations', 'options', variantName], EMPTY.LIST)
          .findIndex(function findOption(item) {
            return item.get('id') === optionId;
          });

        if (optionIndex === -1) return state;

        state = state
          .deleteIn(['data', 'shopsData', shopId, 'variations', 'options', variantName, optionIndex, 'disabled']);
      }
    } else {
      state = state.deleteIn(['data', 'shopsData', shopId, 'variations', 'variants', variantIndex, 'disabled']);
    }
  }

  return state.deleteIn(['data', 'loading', 'variations', shopId]);
}

function* getAllData(reduction, shopId) {
  yield function effect(dispatch) {
    if (!reduction.hasIn(['shops', 'byId', shopId])) return;

    const channel = reduction.getIn(['shops', 'byId', shopId, 'channel']);

    switch (channel) {
      case EBAY: {
        const marketplaceId = reduction.getIn(['shops', 'byId', shopId, 'marketplaceId']);

        if (
          !reduction.getIn(['data', 'loading', 'channel', channel, marketplaceId]) &&
          !reduction.getIn(['data', 'loaded', 'channel', channel, marketplaceId])
        ) {
          dispatch(actions.data.getChannelData({ channel, marketplaceId }));
        }

        break;
      }

      default: {
        if (
          !reduction.getIn(['data', 'loading', 'channel', channel]) &&
          !reduction.getIn(['data', 'loaded', 'channel', channel])
        ) {
          dispatch(actions.data.getChannelData({ channel }));
        }

        break;
      }
    }

    if (
      !reduction.getIn(['data', 'loaded', 'shop', shopId]) &&
      !reduction.getIn(['data', 'loading', 'shop', shopId])
    ) {
      dispatch(actions.data.getShopData({ shopId }));
    }

    if (
      !reduction.getIn(['data', 'loaded', 'profiles', shopId, PROFILE_TYPE.STANDARD]) &&
      !reduction.getIn(['data', 'loading', 'profiles', shopId, PROFILE_TYPE.STANDARD])
    ) {
      dispatch(actions.data.getProfiles({ shopId, type: PROFILE_TYPE.STANDARD }));
    }

    if (reduction.getIn(['user', 'features', FEATURE.AI_PROFILES])) {
      if (
        !reduction.getIn(['data', 'loaded', 'profiles', shopId, PROFILE_TYPE.AI]) &&
        !reduction.getIn(['data', 'loading', 'profiles', shopId, PROFILE_TYPE.AI])
      ) {
        dispatch(actions.data.getProfiles({ shopId, type: PROFILE_TYPE.AI }));
      }
    }

    if (
      !reduction.getIn(['data', 'loaded', 'variations', shopId]) &&
      !reduction.getIn(['data', 'loading', 'variations', shopId])
    ) {
      dispatch(actions.data.getVariations({ shopId }));
    }
  };

  return reduction;
}

function* getChannelData(reduction, { channel, marketplaceId }) {
  switch (channel) {
    case EBAY: {
      if (
        reduction.getIn(['data', 'loaded', 'channel', channel, marketplaceId]) ||
        reduction.getIn(['data', 'loading', 'channel', channel, marketplaceId])
      ) {
        return reduction;
      }

      break;
    }

    default: {
      if (
        reduction.getIn(['data', 'loaded', 'channel', channel]) ||
        reduction.getIn(['data', 'loading', 'channel', channel])
      ) {
        return reduction;
      }

      break;
    }
  }

  if (getSize(CHANNEL_DATA_KEYS[channel]) === 0) {
    return reduction;
  }

  yield function effect(dispatch) {
    function processCountries(countryData) {
      function addData(data, key) {
        dispatch(actions.data.getChannelData.fulfilled({ channel, data, key }));
      }

      forEach(shapeDataForApp.countryData(countryData), addData);
    }

    switch (channel) {
      case EBAY: {
        for (const key of CHANNEL_DATA_KEYS[channel]) {
          if (channel in DATA && marketplaceId in DATA[channel] && key in DATA[channel][marketplaceId]) {
            dispatch(
              actions.data.getChannelData.fulfilled({
                channel,
                marketplaceId,
                data: DATA[channel][marketplaceId][key],
                key,
              })
            );
          }

          switch (key) {
            case 'TAXONOMY': {
              api
                .get({ endpoint: ENDPOINT.S3_TAXONOMY_BUCKET, url: `/ebay/${marketplaceId}/${key}.json` })
                .then(
                  (data) => dispatch(actions.data.getChannelData.fulfilled({ channel, marketplaceId, data, key })),
                  (error) => dispatch(actions.data.getChannelData.rejected({ channel, marketplaceId, error })),
                );

              break;
            }

            default: {
              break;
            }
          }
        }

        break;
      }

      case ETSY: {
        for (const key of CHANNEL_DATA_KEYS[channel]) {
          if (channel in DATA && key in DATA[channel]) {
            dispatch(actions.data.getChannelData.fulfilled({ channel, data: DATA[channel][key], key }));
          }

          api
            .get({ endpoint: ENDPOINT.S3_TAXONOMY_BUCKET, url: `/v1/${key}.json` })
            .then(
              (data) => dispatch(actions.data.getChannelData.fulfilled({ channel, data, key })),
              (error) => dispatch(actions.data.getChannelData.rejected({ channel, error })),
            );
        }

        break;
      }

      case FAIRE: {
        for (const key of CHANNEL_DATA_KEYS[channel]) {
          if (channel in DATA && key in DATA[channel]) {
            dispatch(actions.data.getChannelData.fulfilled({ channel, data: DATA[channel][key], key }));
          }

          switch (key) {
            case 'TAXONOMY': {
              api
                .get({ endpoint: ENDPOINT.S3_TAXONOMY_BUCKET, url: `/faire/${key}.json` })
                .then(
                  (data) => dispatch(actions.data.getChannelData.fulfilled({ channel, data, key })),
                  (error) => dispatch(actions.data.getChannelData.rejected({ channel, error })),
                );

              break;
            }

            case 'EXCHANGE_RATES': {
              api
                .get({ endpoint: ENDPOINT.WEB_V3, url: '/exchange-rate/latest' })
                .then(
                  (data) => dispatch(actions.data.getChannelData.fulfilled({ channel, data, key })),
                  (error) => dispatch(actions.data.getChannelData.rejected({ channel, error })),
                );

              break;
            }

            default: {
              break;
            }
          }
        }

        break;
      }

      case SHOPIFY: {
        for (const key of CHANNEL_DATA_KEYS[channel]) {
          if (channel in DATA && key in DATA[channel]) {
            dispatch(actions.data.getChannelData.fulfilled({ channel, data: DATA[channel][key], key }));
          }

          switch (key) {
            case 'COUNTRIES_LIST':
            case 'COUNTRIES_MAP': {
              break;
            }

            default: {
              api
                .get({ endpoint: ENDPOINT.S3_TAXONOMY_BUCKET, url: `/shopify/${key}.json` })
                .then(
                  (data) => dispatch(actions.data.getChannelData.fulfilled({ channel, data, key })),
                  (error) => dispatch(actions.data.getChannelData.rejected({ channel, error })),
                );

              break;
            }
          }
        }

        if (channel in DATA && 'COUNTRIES_MAP' in DATA[channel]) {
          dispatch(
            actions.data.getChannelData.fulfilled({
              channel,
              data: DATA[channel].COUNTRIES_MAP,
              key: 'COUNTRIES_MAP',
            })
          );

          break;
        }

        api
          .get({ url: '/countries' })
          .then(
            processCountries,
            (error) => dispatch(actions.data.getChannelData.rejected({ channel, error })),
          );

        break;
      }

      default: {
        break;
      }
    }
  };

  switch (channel) {
    case EBAY: {
      return reduction.setIn(['data', 'loading', 'channel', channel, marketplaceId], true);
    }

    default: {
      return reduction.setIn(['data', 'loading', 'channel', channel], true);
    }
  }
}

function* getChannelDataFulfilled(reduction, { channel, marketplaceId, data, key }) {
  addChannelData({ channel, data, key, marketplaceId });

  switch (channel) {
    case EBAY: {
      if (isChannelDataLoaded({ channel, marketplaceId })) {
        return reduction
          .deleteIn(['data', 'loading', 'channel', channel, marketplaceId])
          .setIn(['data', 'loaded', 'channel', channel, marketplaceId], true);
      }

      return reduction;
    }

    default: {
      if (isChannelDataLoaded({ channel })) {
        return reduction
          .deleteIn(['data', 'loading', 'channel', channel])
          .setIn(['data', 'loaded', 'channel', channel], true);
      }

      return reduction;
    }
  }
}

function* getChannelDataRejected(reduction, { channel, marketplaceId, error }) {
  yield function effect(dispatch) {
    console.error('Error encountered while getting channel data: ', error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  switch (channel) {
    case EBAY: {
      return reduction.deleteIn(['data', 'loading', 'channel', channel, marketplaceId]);
    }

    default: {
      return reduction.deleteIn(['data', 'loading', 'channel', channel]);
    }
  }

}

function* getProfiles(reduction, { force = false, shopId, type }) {
  if (
    !reduction.hasIn(['shops', 'byId', shopId]) ||
    reduction.getIn(['data', 'loading', 'profiles', shopId, type]) || (
      !force && reduction.getIn(['data', 'loaded', 'profiles', shopId, type])
    )
  ) {
    return reduction;
  }

  yield function effect(dispatch) {
    const channel = reduction.getIn(['shops', 'byId', shopId, 'channel']);
    const db = reduction.getIn(['shops', 'byId', shopId, 'db']);

    api.profiles
      .getProfiles({ db, shopId, type })
      .then(function onFulfilled({ data }) {
        dispatch(actions.data.getProfiles.fulfilled({ channel, data, shopId, type }));
      })
      .catch(function onRejected(error) {
        dispatch(actions.data.getProfiles.rejected({ error, shopId, type }));
      });
  };

  return reduction.setIn(['data', 'loading', 'profiles', shopId, type], true);
}

function* getProfilesFulfilled(reduction, { channel, data, shopId, type }) {
  const profiles = shapeDataForApp.profiles({ channel, data, type });
  return reduction
    .deleteIn(['data', 'loading', 'profiles', shopId, type])
    .setIn(['data', 'loaded', 'profiles', shopId, type], true)
    .setIn(['data', 'shopsData', shopId, 'profiles', type], profiles);
}

function* getProfilesRejected(reduction, { error, shopId, type }) {
  yield function effect(dispatch) {
    console.error('Error encountered while getting profiles lookup: ', error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_PROFILES,
      })
    );
  };

  return reduction.deleteIn(['data', 'loading', 'profiles', shopId, type]);
}

function* getShopData(reduction, { force = false, shopId }) {
  if (
    reduction.getIn(['data', 'loading', 'shop', shopId]) ||
    !reduction.hasIn(['shops', 'byId', shopId]) || (
      !force && reduction.getIn(['data', 'loaded', 'shop', shopId])
    )
  ) {
    return reduction;
  }

  const channel = reduction.getIn(['shops', 'byId', shopId, 'channel']);
  const params = { db: reduction.getIn(['shops', 'byId', shopId, 'db']) };
  const requests = [];

  switch (channel) {
    case EBAY: {
      requests.push(
        api.get({ endpoint: ENDPOINT.WEB_V2, url: `/ebay/shops/${shopId}/getEbayDetails`, params }),
        api.get({ endpoint: ENDPOINT.WEB_V2, url: `/ebay/shops/${shopId}/policies/payment`, params }),
        api.get({ endpoint: ENDPOINT.WEB_V2, url: `/ebay/shops/${shopId}/policies/return`, params }),
        api.get({ endpoint: ENDPOINT.WEB_V2, url: `/ebay/shops/${shopId}/policies/shipping`, params }),
        api.get({ endpoint: ENDPOINT.WEB_V2, url: `/ebay/shops/${shopId}/customCategories`, params }),
      );

      break;
    }

    case ETSY: {
      requests.push(
        api.get({ url: `/shops/${shopId}/channelData`, params }),
        api.get({ url: `/shops/${shopId}/productionPartners`, params }),
        api.get({ url: `/shops/${shopId}/returnPolicies`, params }),
        api.get({ url: `/shops/${shopId}/shippingTemplates`, params }),
        api.get({ url: `/shops/${shopId}/processingProfiles`, params }),
      );

      break;
    }

    case SHOPIFY: {
      requests.push(
        api.get({ url: `/shops/${shopId}/channelData`, params }),
      );

      break;
    }

    default: {
      break;
    }
  }

  if (getSize(requests) === 0) {
    return reduction
      .setIn(['data', 'loaded', 'shop', shopId], true)
      .setIn(['data', 'shopsData', shopId], reduction.getIn(['data', 'shopsData', shopId], EMPTY.MAP));
  }

  yield function effect(dispatch) {
    Promise.all(requests)
      .then(
        (responses) => dispatch(actions.data.getShopData.fulfilled({ channel, responses, shopId })),
        (error) => dispatch(actions.data.getShopData.rejected({ error, shopId }))
      );
  };

  return reduction.setIn(['data', 'loading', 'shop', shopId], true);
}

function* getShopDataFulfilled(reduction, { channel, responses, shopId }) {
  function updateShopData(shopData = EMPTY.MAP) {
    switch (channel) {
      case EBAY: {
        const [
          { ItemSpecificDetails = {}, VariationDetails = {}} = {},
          { data: paymentPolicies } = {},
          { data: returnPolicies } = {},
          { data: shippingPolicies } = {},
          { data: storeCategories } = {},
        ] = responses;
        const shapedShippingPolicies = shapeDataForApp.ebayShippingPolicies(shippingPolicies);

        return shopData
          .set('storeCategories', shapeDataForApp.storeCategories(storeCategories))
          .set('paymentPolicies', shapeDataForApp.ebayPolicies(paymentPolicies))
          .set('returnPolicies', shapeDataForApp.ebayPolicies(returnPolicies))
          .set('shippingPolicies', shapedShippingPolicies.policies)
          .setIn(['validations', 'shippingPolicies'], shapedShippingPolicies.validations)
          .setIn(['validations', 'itemSpecifics'], shapeDataForApp.validations({ channel, data: ItemSpecificDetails }))
          .setIn(['validations', 'variations'], shapeDataForApp.validations({ channel, data: VariationDetails }));
      }

      case ETSY: {
        const [
          { sectionsMap } = {},
          { productionPartners } = {},
          { returnPolicies } = {},
          { shippingTemplates } = {},
          { processingProfiles } = {},
        ] = responses;

        return shopData
          .set('processingProfiles', shapeDataForApp.processingProfiles(processingProfiles))
          .set('productionPartners', shapeDataForApp.productionPartners(productionPartners))
          .set('returnPolicies', shapeDataForApp.returnPolicies(returnPolicies))
          .set('sections', shapeDataForApp.sections(sectionsMap))
          .set('shippingProfiles', shapeDataForApp.shippingProfiles(shippingTemplates));
      }

      case SHOPIFY: {
        const [{ collections, productTypes, vendors } = {}] = responses;

        return shopData
          .set('collections', shapeDataForApp.collections(collections))
          .set('productTypes', shapeDataForApp.productTypes(productTypes))
          .set('vendors', shapeDataForApp.vendors(vendors));
      }

      default: {
        break;
      }
    }

    return shopData;
  }

  return reduction
    .deleteIn(['data', 'loading', 'shop', shopId])
    .setIn(['data', 'loaded', 'shop', shopId], true)
    .updateIn(['data', 'shopsData', shopId], updateShopData);
}

function* getShopDataRejected(reduction, { error, shopId }) {
  yield function effect(dispatch) {
    console.error('Error encountered while getting shop data: ', error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return reduction.deleteIn(['data', 'loading', 'shop', shopId], true);
}

function* getTaxonomyData(reduction, { category, channel, marketplaceId }) {
  if (
    !category ||
    category === AMOUNT.ZERO ||
    reduction.hasIn(['data', 'loaded', 'taxonomy', channel, marketplaceId, category]) ||
    reduction.hasIn(['data', 'loading', 'taxonomy', channel, marketplaceId, category])
  ) {
    return reduction;
  }

  yield function effect(dispatch) {
    api
      .get({ endpoint: ENDPOINT.S3_TAXONOMY_BUCKET, url: `/${channel}/${marketplaceId}/categories/${category}.json` })
      .then(
        (data) => dispatch(actions.data.getTaxonomyData.fulfilled({ category, channel, data, marketplaceId })),
        (error) => dispatch(actions.data.getTaxonomyData.rejected({ category, channel, marketplaceId, error }))
      );
  };

  function updateLoading(loading = EMPTY.SET) {
    return loading.add(category);
  }

  return reduction.updateIn(['data', 'loading', 'taxonomy', channel, marketplaceId], updateLoading);
}

function* getTaxonomyDataFulfilled(reduction, { category, channel, data, marketplaceId }) {
  addTaxonomyData({ category, channel, data, marketplaceId });

  yield function effect(dispatch) {
    dispatch(actions.data.setData({ path: ['loading', 'taxonomy', channel, marketplaceId, category] }));
  };

  function updateLoaded(loaded = EMPTY.SET) {
    return loaded.add(category);
  }

  return reduction.updateIn(['data', 'loaded', 'taxonomy', channel, marketplaceId], updateLoaded);
}

function* getTaxonomyDataRejected(reduction, { category, channel, marketplaceId, error }) {
  yield function effect(dispatch) {
    dispatch(actions.data.setData({ path: ['loading', 'taxonomy', channel, marketplaceId, category] }));
    console.error(error);
  };

  return reduction;
}

function* getVariations(reduction, { force = false, shopId }) {
  if (
    !reduction.hasIn(['shops', 'byId', shopId]) ||
    reduction.getIn(['data', 'loading', 'variations', shopId]) || (
      !force && reduction.getIn(['data', 'loaded', 'variations', shopId])
    )
  ) {
    return reduction;
  }

  yield function effect(dispatch) {
    const channel = reduction.getIn(['shops', 'byId', shopId, 'channel']);
    const db = reduction.getIn(['shops', 'byId', shopId, 'db']);

    api.variations.getVariations({ channel, db, shopId })
      .then(function onFulfilled(data) {
        dispatch(actions.data.getVariations.fulfilled({ channel, data, shopId }));
      })
      .catch(function onRejected(error) {
        dispatch(actions.data.getVariations.rejected({ error, shopId }));
      });
  };

  return reduction.setIn(['data', 'loading', 'variations', shopId], true);
}

function* getVariationsFulfilled(reduction, { channel, data, shopId }) {
  const variations = shapeDataForApp.variations({ channel, data });
  return reduction
    .deleteIn(['data', 'loading', 'variations', shopId])
    .setIn(['data', 'loaded', 'variations', shopId], true)
    .setIn(['data', 'shopsData', shopId, 'variations'], variations);
}

function* getVariationsRejected(reduction, { error, shopId }) {
  yield function effect(dispatch) {
    console.error('Error encountered while getting variations: ', error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return reduction.deleteIn(['data', 'loading', 'variations', shopId]);
}

function* setData(reduction, { path, value }) {
  const fullPath = Array.isArray(path)
    ? ['data', ...path]
    : ['data', path];

  if (value !== undefined) return reduction.setIn(fullPath, value);

  let state = reduction.deleteIn(fullPath);

  fullPath.pop();

  while (getSize(fullPath) > 1) {
    if (getSize(state.getIn(fullPath)) === 0) {
      state = state.deleteIn(fullPath);
    }

    fullPath.pop();
  }

  return state;
}

const data = {
  [actions.data.addProductType]: addProductType,
  [actions.data.addSection]: addSection,
  [actions.data.addVariations]: addVariations,
  [actions.data.addVariations.rejected]: addVariationsRejected,
  [actions.data.addVendor]: addVendor,
  [actions.data.deleteVariations]: deleteVariations,
  [actions.data.deleteVariations.rejected]: deleteVariationsRejected,
  [actions.data.getAllData]: getAllData,
  [actions.data.getChannelData]: getChannelData,
  [actions.data.getChannelData.fulfilled]: getChannelDataFulfilled,
  [actions.data.getChannelData.rejected]: getChannelDataRejected,
  [actions.data.getProfiles]: getProfiles,
  [actions.data.getProfiles.fulfilled]: getProfilesFulfilled,
  [actions.data.getProfiles.rejected]: getProfilesRejected,
  [actions.data.getTaxonomyData]: getTaxonomyData,
  [actions.data.getTaxonomyData.fulfilled]: getTaxonomyDataFulfilled,
  [actions.data.getTaxonomyData.rejected]: getTaxonomyDataRejected,
  [actions.data.getShopData]: getShopData,
  [actions.data.getShopData.fulfilled]: getShopDataFulfilled,
  [actions.data.getShopData.rejected]: getShopDataRejected,
  [actions.data.getVariations]: getVariations,
  [actions.data.getVariations.fulfilled]: getVariationsFulfilled,
  [actions.data.getVariations.rejected]: getVariationsRejected,
  [actions.data.setData]: setData,
};

export default data;
